import { Badge, Box, Button, Divider, FlexLayout, GridLayout, H4, Icon } from '@deity/falcon-ui';
import { BaseHelmet, CreateEditFlowModal, EntityDetailsHeader } from 'components/index';
import { CACHE_FIRST } from 'contracts/constants';
import type React from 'react';
import { useEffect, useState } from 'react';
import { Link, useLocation, useRouteMatch } from 'react-router-dom';
import { useGetFlowLazyQuery, useGetPackageVersionQuery } from 'services/graphql/generated/hooks';
import { isPackageEditable } from 'utils/is-package-editable';

type FlowOverViewProps = {
  params: any;
};

export const FlowOverview: React.FC<FlowOverViewProps> = ({ params }) => {
  const { pathname } = useLocation();
  const { url } = useRouteMatch();
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);

  const [getFlow, { loading: loadingFLow, data: flowData, refetch }] = useGetFlowLazyQuery({
    fetchPolicy: 'cache-first'
  });

  useEffect(() => {
    if (params?.entityId) {
      getFlow({
        variables: {
          packageVersionUuid: params.versionUuid,
          uuid: params.entityId
        }
      });
    }
  }, [pathname]);
  const { data, loading } = useGetPackageVersionQuery({
    variables: {
      uuid: params?.versionUuid
    },
    skip: !params?.versionUuid,
    ...CACHE_FIRST
  });

  const isEditable: boolean = data?.getPackageVersion.status
    ? isPackageEditable(data?.getPackageVersion.status)
    : false;

  return (
    <>
      <BaseHelmet page={flowData?.getFlow?.name ?? 'Flow'} />
      <EntityDetailsHeader
        isLoading={loadingFLow}
        name={flowData?.getFlow?.name}
        description={flowData?.getFlow?.description}
        isEditable={isEditable}
        onModalOpen={setIsModalOpen}
        endEnhancer={
          <Badge bg="secondary" fontWeight="bold" color="white" alignSelf="flex-start">
            <FlexLayout alignItems="center">
              <Icon src="lock" stroke="transparent" style={{ height: '18px' }} />
              <H4 fontWeight="semiBold" fontSize="sm">
                EDITING
              </H4>
            </FlexLayout>
          </Badge>
        }
      />
      <Divider borderColor="secondary" />
      <Box p="xxl">
        <H4 fontWeight="bold" fontSize="xl" mb="md">
          Activity
        </H4>
        <GridLayout gridTemplateColumns="1fr 1fr">
          <Box>
            <H4 fontWeight="bold" fontSize="md">
              Started
            </H4>
            <FlexLayout mt="xs" alignItems="center">
              <H4 fontSize="lg">2021-01-01 13:25:55</H4>
              <Icon ml="xs" src="checkCircle" stroke="transparent" color="green" />
            </FlexLayout>
          </Box>
          <Box>
            <H4 fontWeight="bold" fontSize="md">
              Run Time
            </H4>
            <FlexLayout mt="xs">
              <H4 fontSize="lg">3h:15m:55s</H4>
            </FlexLayout>
          </Box>
        </GridLayout>
        <FlexLayout mt="xl" alignItems="center">
          <Button variant="border" mr="xs" color="error" borderColor="error">
            <Icon src="stop" mr="xs" stroke="transparent" />
            Stop
          </Button>

          <Link
            to={`${url.slice(0, url.lastIndexOf('/'))}/editor/${params.entityId}`}
            style={{ textDecoration: 'none' }}
          >
            <Button variant="border">
              <Icon src="edit" mr="xs" />
              Flow editor
            </Button>
          </Link>
          <FlexLayout ml="xs">
            <Icon src="help" color="secondary" stroke="white" />
            <H4 ml="xs" color="secondary">
              Flow editor is in read-only mode when the flow is running.
            </H4>
          </FlexLayout>
        </FlexLayout>
      </Box>
      <CreateEditFlowModal
        versionUuid={params.versionUuid}
        isEdit
        isOpen={isModalOpen}
        editFlow={flowData}
        setOpenState={state => setIsModalOpen(state)}
        onPackageCreated={() => {
          if (refetch) {
            refetch().then();
          }
        }}
      />
    </>
  );
};
